// English strings for P6-A (PAYMENT-01/03/05/08/09, FINANCE-01/03/04) (D-013: English only). Owned by that worker;
// spread into `en` as `en.payments`, so keys read `t("payments.…")`. Copy rules: DESIGN_SYSTEM §9.6.
export const paymentsStrings = {
  // The eSewa hand-off page (a form served on the store host that sends itself; the button is the
  // fallback when the browser blocks the script).
  handoff: {
    title: "Taking you to eSewa",
    body: "Sign in to eSewa to pay. Your order is confirmed once eSewa tells us the payment went through.",
    continue: "Pay with eSewa",
  },

  tabs: {
    online: "Checkout attempts",
    settlements: "Settlements",
    deposits: "Deposits",
  },

  gateway: {
    credentialsTitle: "Credentials",
    credentialsHelp:
      "Paste the keys from your {provider} merchant account. They are encrypted and can't be shown again; only the last 4 characters appear here.",
    stored: "Saved, ending in {last4}",
    storedShort: "Saved",
    notStored: "Not added yet",
    replace: "Replace",
    field: {
      product_code: "Merchant code",
      secret_key: "Secret key",
      merchant_code: "Merchant code",
      // D-122: Fonepay checkout's merchant code (PID), as Fonepay names it.
      merchant_id: "Merchant ID",
      username: "Username",
      password: "Password",
    },
    mode: "Mode",
    modeTest: "Test (sandbox)",
    modeLive: "Live",
    liveOnlyProduction: "Live mode is available only on the production site.",
    save: "Save {provider}",
    saved: "{provider} settings saved",
    test: "Test connection",
    testOk: "{provider} answered. The key works.",
    testReachable: "{provider} answered. Make one small test payment to confirm the key.",
    testFailed: "{provider} refused the key. Check it and save it again.",
    pendingSpec: "Fonepay QR needs the integration documents from your bank first.",
    missing: "Add the credentials to turn this on.",
    stateActive: "Ready",
    statePending: "Waiting for credentials",
  },

  sessions: {
    caption: "Online payments that started at checkout",
    lead: "Payments are confirmed only by the provider's own check. Pending ones are checked again every 15 minutes for a day, then daily for a week.",
    emptyTitle: "No online payments yet",
    emptyBody: "When a customer pays by eSewa or Khalti, the attempt shows here.",
    columns: {
      order: "Order",
      method: "Method",
      state: "Status",
      checks: "Checks",
      next: "Next check",
      amount: "Amount",
    },
    state: {
      created: "Starting",
      pending: "Waiting for the provider",
      paid: "Paid",
      failed: "Failed",
      expired: "Expired",
      cancelled: "Cancelled",
    },
    reason: {
      amount_mismatch: "Amount didn't match",
      missing_provider_txn: "No transaction from the provider",
      reconcile_window_over: "No answer after 8 days",
      init_incomplete: "Didn't start",
    },
    checkNow: "Check now",
    checked: "Checked with the provider: {state}",
    testBadge: "Test",
    never: "Not scheduled",
  },

  refund: {
    action: "Refund",
    title: "Refund order {number}",
    body: "Up to {amount} can be refunded. Gateway refunds go back to the customer's wallet; others are recorded here.",
    amount: "Amount (Rs)",
    method: "Refund by",
    methods: {
      original: "Original payment method",
      cash: "Cash",
      bank_transfer: "Bank transfer",
      store_credit: "Store credit",
    },
    reason: "Reason",
    reasonPlaceholder: "For example: one item arrived damaged",
    submit: "Refund {amount}",
    done: "Refund recorded",
    pending: "Refund sent to the provider",
    failed: "The provider didn't accept the refund. Nothing was refunded.",
    tooMuch: "That's more than was paid on this order.",
  },

  deposits: {
    title: "Advance deposits",
    lead: "Ask for part of the total before dispatch. The courier collects the rest.",
    emptyTitle: "No deposit rules",
    emptyBody: "Add a rule to ask for an advance on large or risky cash-on-delivery orders.",
    add: "Add rule",
    kind: "Deposit",
    kinds: {
      fixed: "Fixed amount",
      percent: "Percent of the total",
      delivery_fee: "The delivery fee",
    },
    amount: "Amount (Rs)",
    percent: "Percent",
    minTotal: "Only for orders over (Rs)",
    priority: "Priority",
    describeFixed: "{amount} deposit",
    describePercent: "{percent} % deposit",
    describeDelivery: "Delivery fee as deposit",
    describeOver: "for orders over {amount}",
    active: "On",
    inactive: "Off",
    remove: "Remove",
    removed: "Rule removed",
    added: "Rule added",
  },

  settlements: {
    caption: "Gateway payouts to your bank",
    emptyTitle: "No settlements yet",
    emptyBody:
      "When eSewa or Khalti pays out, record the payout here to move it into your bank balance.",
    unsettledTitle: "Waiting for payout",
    unsettledEmpty: "Every online payment is settled.",
    add: "Record payout",
    provider: "Provider",
    reference: "Payout reference",
    settledOn: "Paid out on",
    lines: "Transactions",
    linesHelp: "One per line: transaction ID, amount (Rs), fee (Rs).",
    match: "Match and post",
    matched: "Payout matched and posted",
    state: { imported: "Recorded", matched: "Matched" },
    lineMatch: {
      matched: "Matched",
      unknown: "Not found",
      amount_mismatch: "Amount differs",
      already_settled: "Already settled",
    },
  },

  cod: {
    title: "COD",
    lead: "Record what each courier pays you. Orders count as paid when the money arrives.",
    summaryTitle: "By courier",
    summaryPending: "Expected and owed amounts appear once courier shipments are connected.",
    remittancesTitle: "Remittances",
    emptyTitle: "No remittances yet",
    emptyBody: "When a courier pays you for delivered parcels, record it here.",
    add: "Record remittance",
    courierAccount: "Courier account ID",
    reference: "Remittance reference",
    receivedOn: "Received on",
    amount: "Amount received (Rs)",
    parcels: "Parcels",
    parcelsHelp:
      "One per line: tracking number, collected (Rs), courier fee (Rs), return fee (Rs).",
    match: "Match parcels",
    post: "Post",
    matched: "Parcels matched",
    posted: "Remittance posted",
    readerPending: "Parcel matching starts when courier shipments are connected.",
    state: { imported: "Recorded", matched: "Matched", posted: "Posted" },
    lineMatch: {
      matched: "Matched",
      short: "Short",
      over: "Over",
      unknown: "Unknown parcel",
      already_remitted: "Already remitted",
    },
    shortfallsTitle: "Shortfalls",
    shortfallsEmpty: "No short parcels.",
    columns: {
      courier: "Courier",
      remitted: "Remitted",
      collected: "Collected",
      fees: "Fees",
      short: "Short",
      tracking: "Tracking",
      expected: "Expected",
      match: "Result",
    },
  },

  finance: {
    title: "Earnings",
    lead: "Every payment, refund, payout and remittance posts here. Entries never change; a mistake is fixed by reversing it.",
    tabs: { cashBook: "Cash book", accounts: "Accounts", journal: "Journal" },
    overviewTitle: "Finance overview",
    overviewLead: "Money in and out of your accounts, and the COD your couriers still hold.",
    sections: {
      label: "Money sections",
      overview: "Overview",
      payments: "Payments",
      journal: "Daybook",
      accounts: "Cash and bank",
      expenses: "Expenses",
      cod: "COD",
      suppliers: "Suppliers",
      statements: "Statements",
    },
    actions: {
      moneyIn: "Money in",
      moneyOut: "Money out",
      transfer: "Transfer",
      addExpense: "Add expense",
    },
    range: {
      label: "Period",
      d7: "Last 7 days",
      d30: "Last 30 days",
      d90: "Last 90 days",
      span: "{from} – {to}",
    },
    kpis: {
      moneyIn: "Money in",
      moneyOut: "Money out",
      net: "Net",
      balance: "Total balance",
      balanceHint: "Every account, end of the period",
    },
    byAccount: {
      title: "Money in and out by account",
      sub: 'The six biggest; the rest are "Other"',
      other: "Other",
      in: "Money in",
      out: "Money out",
      empty: "No money moved in this period.",
    },
    accountsCard: { title: "Accounts", sub: "Balance at the end of the period" },
    from: "From",
    to: "To",
    columns: {
      account: "Account",
      opening: "Opening",
      in: "In",
      out: "Out",
      closing: "Closing",
      balance: "Balance",
      when: "When",
      memo: "Details",
      debit: "Debit",
      credit: "Credit",
    },
    account: {
      cash: "Cash on hand",
      drawer: "POS drawers",
      bank: "Bank",
      clearing: "Gateway clearing",
      courier_receivable: "COD with couriers",
      sales: "Sales",
      shipping_income: "Delivery income",
      discounts: "Discounts",
      vat_payable: "VAT payable",
      refunds: "Refunds",
      fees: "Gateway and courier fees",
      cod_shortfall: "COD shortfalls",
      cod_suspense: "Unmatched COD money",
      store_credit_liability: "Store credit owed",
      over_short: "Drawer over/short",
    },
    emptyEntries: "No entries in this period.",
    addAccount: "Add account",
    accountCode: "Code",
    accountName: "Name",
    accountKind: "Type",
    kinds: {
      cash: "Cash",
      bank: "Bank",
      wallet: "Wallet",
      expense: "Expense",
      revenue: "Income",
      payable: "Payable",
      equity: "Owner's equity",
      liability: "Liability",
    },
    openingBalance: "Opening balance (Rs)",
    accountAdded: "Account added",
    transfer: "Move money",
    transferFrom: "From account",
    transferTo: "To account",
    transferAmount: "Amount (Rs)",
    transferMemo: "Details",
    transferred: "Entry posted",
    reverse: "Reverse",
    reversed: "Entry reversed",
    reversal: "Reversal",
    sameAccount: "Choose two different accounts.",
    unbalanced: "Debits and credits must be equal.",
  },

  // Money pages (owner, 2026-10-22): popups, Daybook, Cash and bank, Expenses, Suppliers, Statements.
  money: {
    close: "Close",
    cancel: "Cancel",
    addShort: "Add",
    edit: "Edit",
    showAll: "Show everything",
    loadMore: "Load older entries",
    total: "Total",
    builtIn: "Built in",
    archived: "Archived",
    archivedToast: "{name} archived. Its history stays.",
    restored: "{name} is back in use",
    reversedTag: "Reversed",
    youOwe: "You owe",
    billed: "Billed",
    paid: "Paid",
    history: "History",
    noHistory: "Nothing has moved through this account yet.",
    openingIncluded: "The balance includes the opening balance of {amount}.",
    openingOwed: "Includes {amount} owed from before.",
    netUp: "More came in than went out",
    netDown: "More went out than came in",
    owedHint: "You owe suppliers {amount}",
    amountDate: "Amount and date",
    amount: "Amount (Rs)",
    date: "Date",
    intoAccount: "Into",
    fromWhere: "Where it came from",
    whatFor: "What it was for",
    category: "Category",
    paidFrom: "Paid from",
    fromAccount: "From",
    toAccount: "To",
    supplier: "Supplier",
    howPaid: "Payment",
    paidNow: "Paid now",
    paidLater: "On credit (pay later)",
    details: "Details",
    notePh: "Note (optional), e.g. what it was",
    billNoPh: "Bill or invoice number (optional)",
    billRef: "Bill {ref}",
    newCategory: "New category",
    newIncome: "New income type",
    newSupplier: "New supplier",
    newNamePh: "Name",
    noSuppliers: "No suppliers yet. Add one here.",
    previewHint: "Fill in the amount and accounts to see the entry.",
    fixHint:
      "Entries can't be edited. If something is wrong, open it in the Daybook and reverse it.",
    goesNegative: "This takes the account below zero. Check the amount.",
    memo: {
      in: "Money in: {what}",
      out: "Money out: {what}",
      bill: "Bill from {supplier}: {what}",
      pay: "Paid {supplier}",
      transfer: "Moved money from {from} to {to}",
    },
    need: {
      amount: "Enter an amount",
      date: "The date can't be in the future",
      in: "Pick the account and where the money came from",
      out: "Pick what it was for and the account it was paid from",
      transfer: "Pick two different accounts",
      expense: "Pick a category and how it was paid",
      bill: "Pick the supplier and a category",
      pay: "Pick the supplier and the account you paid from",
    },
    move: {
      in: { title: "Money in", save: "Add money in", done: "{amount} added" },
      out: { title: "Money out", save: "Record money out", done: "{amount} recorded as money out" },
      transfer: { title: "Transfer", save: "Move money", done: "{amount} moved" },
      expense: { title: "Add expense", save: "Add expense", done: "Expense of {amount} added" },
      bill: { title: "Record a bill", save: "Record bill", done: "Bill of {amount} recorded" },
      pay: { title: "Pay supplier", save: "Record payment", done: "Payment of {amount} recorded" },
    },
    kinds: {
      in: "Money in",
      out: "Money out",
      transfer: "Transfer",
      expense: "Expense",
      bill: "Bill",
      supplierPayment: "Supplier paid",
      sale: "Sale",
      refund: "Refund",
      cod: "COD",
      settlement: "Settlement",
      reversal: "Reversal",
      other: "Adjustment",
    },
    moneyKinds: {
      cash: "Cash",
      bank: "Bank account",
      wallet: "Wallet",
      drawer: "POS drawers",
      clearing: "Online payments not settled yet",
      courier_receivable: "COD still with couriers",
    },
    groups: {
      asset: "Money",
      income: "Income",
      expense: "Expense",
      owed: "You owe",
      owner: "Owner",
    },
    columns: {
      when: "Date",
      details: "Details",
      type: "Type",
      amount: "Amount",
      actions: "Actions",
    },
    menu: {
      label: "Actions for {memo}",
      open: "Open entry",
      accountLabel: "Actions for {name}",
      history: "Open history",
      archive: "Archive",
      restore: "Use again",
    },
    period: {
      label: "Period",
      thisMonth: "This month",
      lastMonth: "Last month",
      d30: "30 days",
      d90: "90 days",
      d365: "12 months",
      lifetime: "Lifetime",
      custom: "Custom dates",
    },
    recent: {
      title: "Recent activity",
      sub: "The latest entries in your books",
      all: "Open the Daybook",
    },
    daybook: {
      caption: "Daybook entries",
      filters: "Entry type",
      filter: {
        all: "All",
        in: "Money in",
        out: "Money out",
        expense: "Expenses",
        transfer: "Transfers",
        supplier: "Suppliers",
        sale: "Sales",
        reversal: "Reversals",
      },
      searchLabel: "Search the Daybook",
      searchPh: "Details or account",
      noMatchTitle: "Nothing matches",
      noMatchBody: "Try another type or search.",
    },
    entry: {
      title: "Entry",
      lines: "Accounts",
      debit: "In / debit",
      credit: "Out / credit",
      linesHelp: "Every entry balances: the in side equals the out side.",
      byHand: "Added by hand on the Money pages.",
      automatic: "Posted automatically by an order, payment, settlement or remittance.",
      isReversed: "This entry was reversed; it no longer counts.",
      isReversal: "This entry cancels an earlier one.",
      confirmTitle: "Reverse this entry?",
      confirmBody: "A new entry moves {amount} back. Both stay in the Daybook.",
      confirm: "Yes, reverse it",
    },
    cash: {
      total: "All money",
      totalHint: "Every money account today",
      inHand: "Cash and bank",
      inHandHint: "Cash, bank, wallets and POS drawers",
      onTheWay: "On its way",
      onTheWayHint: "Online payments and COD not yet received",
      views: "Accounts view",
      viewMoney: "Money accounts",
      viewAll: "All accounts",
      add: "Add",
      take: "Take out",
      move: "Move",
      auto: {
        clearing: "Moves by itself when gateways settle",
        courier_receivable: "Moves by itself when couriers remit",
      },
      searchLabel: "Search accounts",
      searchPh: "Account name",
      noMatch: "No account has that name.",
    },
    account: {
      add: {
        money: "Add account",
        expense: "Add category",
        income: "Add income type",
        supplier: "Add supplier",
      },
      edit: {
        money: "Edit account",
        expense: "Edit category",
        income: "Edit income type",
        supplier: "Edit supplier",
      },
      sec: {
        money: "Account",
        expense: "Expense category",
        income: "Income type",
        supplier: "Supplier",
      },
      namePh: {
        money: "Account name, e.g. NIC Asia Bank",
        expense: "Category name, e.g. Shop rent",
        income: "Income type, e.g. Workshop fees",
        supplier: "Supplier name",
      },
      role: {
        money: "Money account",
        expense: "Expense category",
        income: "Income type",
        supplier: "Supplier",
      },
      hint: {
        money: "Use one account for each bank account, wallet or cash box you keep.",
        expense: "Group your spending so the profit and loss shows where money goes.",
        income: "Money that isn't from orders, like a workshop or a grant.",
        supplier: "Record bills from this supplier and what you pay them.",
      },
      ideas: {
        rent: "Shop rent",
        salary: "Staff salary",
        packaging: "Packaging",
        stock: "Stock purchases",
        ads: "Ads and marketing",
        utilities: "Electricity and internet",
        transport: "Transport",
      },
      opening: {
        money: "Money in it today",
        supplier: "You already owe them",
      },
      openingHelp: {
        money: "The balance today. Later money in and out is added to it.",
        supplier: "What you owe from before. Leave empty if nothing.",
      },
      contact: "Contact",
      contactHint: "Phone and PAN show here",
      phone: "Phone",
      phonePh: "Phone",
      pan: "PAN / VAT",
      panPh: "PAN or VAT number",
      address: "Address",
      addressPh: "Address",
      note: "Note",
      notePh: "Note, e.g. pays on the 15th",
      needName: "Enter a name",
      save: "Save",
      added: "{name} added",
      saved: "{name} saved",
      editHint: "Its history doesn't change. Archive it from the list when you stop using it.",
      changed: "Someone changed this meanwhile. Close and open it again.",
    },
    expenses: {
      lead: "Rent, salaries, packaging and everything else you spend, by category.",
      spent: "Spent",
      spentHint: "All expense categories in this period",
      biggest: "Biggest category",
      none: "No expenses in this period",
      onCredit: "Bought on credit",
      onCreditHint: "Bills from suppliers in this period",
      count: "Expenses",
      countHint: "Entries in this period",
      categories: "Categories",
      categoriesSub: "Tap one to see only its expenses",
      noCategories: "No categories yet.",
      newCategory: "New category",
      searchLabel: "Search expenses",
      searchPh: "Details or account",
      emptyTitle: "No expenses in this period",
      emptyBody: "Add rent, salaries, packaging and other spending to see your real profit.",
    },
    suppliers: {
      lead: "Who you buy from, what they billed, what you paid and what you still owe.",
      owe: "You owe",
      advance: "Paid ahead",
      settled: "Settled",
      totalOwed: "You owe",
      owingCount: { one: "To {count} supplier", other: "To {count} suppliers" },
      totalBilled: "Billed",
      totalPaid: "Paid",
      allTime: "All time",
      count: "Suppliers",
      countHint: "In use",
      views: {
        label: "Supplier view",
        all: "All",
        owe: "You owe",
        settled: "Settled",
        archived: "Archived",
      },
      columns: {
        name: "Supplier",
        phone: "Phone",
        pan: "PAN / VAT",
        status: "Status",
      },
      call: "Call",
      copy: "Copy phone",
      statement: "Open statement",
      searchLabel: "Search suppliers",
      searchPh: "Name, phone or PAN",
      noMatch: "Try another view or search.",
      emptyTitle: "No suppliers yet",
      emptyBody: "Add the people and shops you buy stock and services from.",
    },
    cod: {
      views: "COD view",
      remitted: "Remitted to you",
      remittedHint: { one: "{count} remittance", other: "{count} remittances" },
      fees: "Courier charges",
      feesHint: "Delivery and return fees taken",
      short: "Short",
      shortHint: { one: "{count} parcel paid less", other: "{count} parcels paid less" },
      waiting: "Waiting to post",
      waitingHint: "Recorded but not posted to your books",
      parcelsCol: "Parcels",
      open: "Open remittance",
      matchFirst: "Match the parcels first",
      shortEmptyBody: "Parcels a courier paid less for than the order total show here.",
      noCouriers: "No courier is connected yet.",
      connect: "Connect a courier",
      payment: "Payment",
      referencePh: "Reference on the courier statement",
      amountPh: "Amount received",
      paste: "Paste from file",
      addLines: "Add these parcels",
      returnFee: "Return fee",
      addRow: "Add parcel",
      removeRow: "Remove parcel {n}",
      pickCourier: "Pick the courier",
      charges: "Courier charges",
      expected: "You should receive",
      matches: "Matches the amount received",
      less: "You received {amount} less than the parcels add up to",
      more: "You received {amount} more than the parcels add up to",
      hint: "Add the parcels from the courier statement to check the amount.",
      recorded: "Remittance of {amount} recorded",
      noLines: "No parcels on this remittance.",
      notMatched: "Not matched",
      need: {
        courier: "Pick the courier",
        reference: "Enter the remittance reference",
        amount: "Enter the amount received",
        parcels: "Add at least one parcel",
        badRow:
          "Each parcel needs a tracking number (letters, digits, - . _) and the amount collected",
      },
      stepHelp: {
        imported: "Next: match the parcels to your COD orders, then post it to your books.",
        matched: "Parcels are matched. Post it to mark the orders paid and move the money.",
        posted: "Posted: the orders are paid and the money is in your books.",
      },
    },
    decide: {
      verifyNotes: {
        bank: "Matched in the bank app",
        wallet: "Matched in the wallet app",
        cash: "Received in cash",
      },
      rejectNotes: {
        none: "No money received",
        amount: "Amount is different",
        unclear: "Screenshot isn't clear",
      },
    },
    refund: {
      full: "Full {amount}",
      half: "Half",
      reasons: {
        damaged: "Item arrived damaged",
        wrong: "Wrong item sent",
        cancelled: "Customer cancelled",
        late: "Delivered late",
      },
      paid: "Paid on the order",
      refunding: "Refunding",
      left: "Left after refund",
      hint: "Gateway refunds go back to the customer's wallet. Cash, bank and store credit refunds are recorded here.",
    },
    qr: {
      labelPh: "Name shown to customers, e.g. Himal Threads eSewa",
      holderPh: "Account holder name",
      instructionsPh: "e.g. Put your order number in the remarks",
      preview: "At checkout",
      previewName: "Your QR",
      hint: "Customers see this QR at checkout and pay to it.",
      need: "Enter the name and the account holder, and add the QR image",
      image: {
        title: "QR image",
        add: "Add QR image",
        replace: "Replace QR image",
        uploading: "Uploading…",
        help: "A clear photo or screenshot of your wallet or bank QR (JPEG, PNG or WebP).",
        saved: "QR image saved.",
        type: "Use a JPEG, PNG or WebP image.",
        size: "The image is too large. Use one under 15 MB.",
      },
    },
    deposit: {
      when: "Only for larger orders",
      minPh: "Orders over (optional)",
      minHelp: "Leave empty to ask every cash-on-delivery order.",
      deliveryHelp: "The customer pays the delivery fee first; the rider collects the rest.",
      needPercent: "Enter a percent between 1 and 100",
      rule: "Rule",
      exampleOrder: "Order of Rs 5,000",
      payNow: "Paid before dispatch",
      onDelivery: "Collected on delivery",
    },
    settle: {
      payout: "Payout",
      txn: "Transaction ID",
      addRow: "Add transaction",
      addLines: "Add these transactions",
      count: "Transactions",
      gross: "Paid by customers",
      net: "Paid to your bank",
      hint: "Then match it: the payments move from gateway clearing to your bank.",
      recorded: "Payout of {amount} recorded",
      needRef: "Enter the payout reference",
      needLines: "Add at least one transaction",
      badRow: "Each transaction needs its ID (4–80 letters or digits) and amount",
    },
    statements: {
      lead: "Profit and loss, what you own and owe, and the trial balance for any period.",
      views: "Statement",
      view: { pnl: "Profit & loss", balance: "Balance sheet", trial: "Trial balance" },
      income: "Income",
      less: "Less returns and discounts",
      // D-106: this view counts money received; Home and Reports' "Net sales" counts orders placed.
      netSales: "Sales received",
      expenses: "Expenses",
      totalExpenses: "Total expenses",
      profit: "Profit",
      loss: "Loss",
      margin: "Profit margin",
      marginHint: "Profit as a share of sales received",
      vsPrev: "{pct} on the period before",
      prevNone: "Nothing in the period before",
      nothing: "Nothing in this period",
      pnlNote:
        "A cash view: sales count when the money is received (cash on delivery when the courier pays it in), and stock bought counts as an expense when it is bought. Home and Reports count orders when they are placed.",
      asOf: "On {date}",
      assets: "What you have",
      liabilities: "What you owe",
      liabilitiesEquity: "What you owe and what's yours",
      equity: "What's yours",
      totalAssets: "Total you have",
      totalLiabilitiesEquity: "Total",
      netWorth: "Worth of the business",
      netWorthHint: "What you have less what you owe",
      profitSoFar: "Profit so far",
      openingBalances: "Opening balances",
      balanceNote:
        "Stock on the shelf isn't counted yet; inventory value comes with stock costing.",
      totals: "Totals",
      trialNote:
        "Each account's balance on its side. Opening balances entered on accounts appear as one line so the columns match.",
      export: "Export CSV",
      print: "Print",
      csv: { section: "Section" },
      file: { pnl: "profit-and-loss", balance: "balance-sheet", trial: "trial-balance" },
    },
  },

  errors: {
    amount: "Enter an amount in rupees, for example 1250 or 1250.50.",
    lines: "Check the lines: each needs the values the help text lists.",
    generic: "That didn't work. Check the details and try again.",
  },
  // Apps → Payments (owner, 2026-10-23): every way customers can pay, as cards.
  methods: {
    lead: "Choose how customers pay you. Turn a method on once it is set up.",
    active: "Active",
    activeHelp: "Customers can pay with these.",
    notSetUp: "Not set up",
    notSetUpHelp: "Add your details to start using these.",
    noneActive: "No payment method is on. Turn on Cash on Delivery or set up a wallet below.",
    note: "Payments go straight to your own eSewa, Khalti or bank account. Adyvance never holds your money.",
    manageAreas: "Manage areas",
    setUp: "Set up",
    manage: "Manage",
    close: "Close",
    enableHint: "Shown at checkout once the keys are saved.",
    keysNote:
      "Your {provider} merchant keys are stored encrypted and never shown again; only the last 4 characters are.",
    form: {
      title: "Set up {name}",
      status: "Status",
      keys: "Merchant keys",
      keysRow: "Keys saved",
      saved: "{n} of {total} saved",
      ending: "Saved, ends {last4}",
      show: "Show",
      hide: "Hide",
      placeholder: {
        product_code: "For example EPAYTEST",
        secret_key: "Paste the secret key",
        merchant_code: "The merchant code from Fonepay",
        merchant_id: "For example 2222666677778888",
        username: "The username from Fonepay",
        password: "The password from Fonepay",
      },
      where: "Find these in your {name} merchant dashboard, under API or developer settings.",
      // D-122: Fonepay hosted checkout.
      fonepayWebKeys:
        "Fonepay gives the merchant ID and secret key when it opens web checkout for your merchant account (ask Fonepay or your bank). Customers pay on Fonepay's page and come back to your store; Adyvance confirms every payment with Fonepay before the order is marked paid.",
      // docs.khalti.com Getting Started (2026-10-30): new merchants are capped until Khalti's KYC check.
      stripeKeys:
        "Paste your own Stripe secret key from Stripe Dashboard → Developers → API keys: sk_test_… in test mode, sk_live_… in live mode (a restricted key, rk_…, with Checkout Sessions and Refunds write access, works too). The money goes to your Stripe account. Stripe doesn't open accounts for businesses registered in Nepal, so live card payments need a Stripe account from a supported country.",
      khaltiLimit:
        "A new Khalti merchant account takes up to Rs 200 per payment until Khalti has checked your business details. Ask Khalti to lift the limit before taking bigger orders.",
      encrypted: "Keys are stored encrypted and never shown again; only the last 4 characters are.",
      save: "Save",
      done: "Done",
      accounts: "QR accounts",
      qrImage: "QR added",
      howTitle: "How customers pay",
      qrSteps: {
        one: "They choose Manual QR at checkout and see your QR.",
        two: "They pay in their app and upload a screenshot.",
        three: "You check it in Earnings → Online payments and confirm the order.",
      },
      whatTitle: "What it does",
    },
    keysNeeded: "Enter the keys first.",
    qrHint: "Customers see your QR at checkout and upload a screenshot that you check.",
    state: {
      active: "Active",
      off: "Off",
      setup: "Setup needed",
      error: "Needs attention",
      spec: "Waiting for Fonepay",
      platform: "Not available yet",
    },
    names: {
      cod: "Cash on Delivery",
      khalti: "Khalti",
      esewa: "eSewa",
      fonepay_qr: "Fonepay QR",
      fonepay_web: "Fonepay",
      fonepay_intent: "Fonepay Intent",
      manual_qr: "Manual QR",
      stripe: "Stripe",
    },
    about: {
      cod: "Customers pay in cash when the parcel arrives.",
      khalti: "Customers pay in the Khalti app or website and come back to your store.",
      esewa: "Customers sign in to eSewa to pay, then come back to your store.",
      fonepay_qr: "Customers scan one QR with any bank or wallet app (eSewa, Khalti, banks).",
      fonepay_web:
        "Fonepay hosted checkout: customers pay on Fonepay's page with any bank, wallet or card, then come back to your store.",
      fonepay_intent: "Opens the customer's mobile banking app directly to pay.",
      manual_qr: "Show your own QR codes. Customers send a screenshot and you check the payment.",
      stripe:
        "Customers pay by card on Stripe's secure page, including from abroad. The money goes to your own Stripe account.",
    },
    how: {
      fonepay_qr: {
        one: "A fresh QR for each order, with the exact amount.",
        two: "Works with every Nepali bank and wallet app.",
        three: "The payment confirms itself; no screenshots to check.",
      },
      fonepay_intent: {
        one: "Customers on a phone tap Pay and their banking app opens.",
        two: "Uses an RSA key pair: the public key goes to Fonepay, the private key stays encrypted here.",
        three: "The payment confirms itself when the customer returns.",
      },
      stripe: {
        one: "Card payments from customers in Nepal and abroad.",
        two: "You connect your own Stripe account; Adyvance never holds the money.",
        three: "Card details are entered on Stripe's page, never on your store.",
      },
    },
    waiting: {
      spec: "Fonepay shares its merchant integration guide only with signed partners. This switches on once Adyvance has it; nothing is charged meanwhile.",
      platform:
        "Stripe needs an Adyvance platform account first, and Stripe does not yet sign up businesses in Nepal directly. This stays off until that is in place.",
    },
  },
} as const;
